import qs from 'qs';

import { HOST_QUERY_KEY, iamApiInstance } from '@kc-console/context';

import { CookieKeyMap } from '../../../constants/cookie';
import { SettingsRoutePath } from '../../../constants/guards/settings';
import { appendContextCookies } from '../../../utils/cookie/appendContextCookies';
import { createSetCookieString } from '../../../utils/cookie/createSetCookieString';
import { expireSetCookieString } from '../../../utils/cookie/expireSetCookieString';
import { parseCookies } from '../../../utils/cookie/parseCookies';
import { mergePathWithQuery } from '../../../utils/mergePathWithQuery';
import { createServerTraceGroupId, startServerTraceSpan } from '../../../utils/serverTrace/store';

import type { GetServerSidePropsContext } from 'next';
import type { QueryClient } from '@tanstack/react-query';
import type { StatusCode } from '@kc-console/core';

/**
 * SSO 로그인 여부 확인
 * @description
 * - SSO 로그인 여부를 확인합니다.
 * - SSO 로그인 여부를 확인하기 위해서는 조직 정보를 불러와야 합니다.
 * - 조직 정보를 불러오는 데 실패한 경우, false를 반환합니다.
 * - 조직 정보를 불러오는 데 성공한 경우, sso_enabled를 반환합니다. (true일 때, 해당 조직은 IdP 로그인을 사용하므로 settings 서비스에서 비밀번호 재확인을 처리할 수 없는 케이스를 의미합니다.)
 */
const checkSsoEnabled = async ({
  domainId,
  queryClient,
  token,
  iamEndpoint,
  request,
  traceGroupId,
}: {
  domainId: string;
  iamEndpoint: string;
  queryClient: QueryClient;
  request: GetServerSidePropsContext['req'];
  token: string;
  traceGroupId: string;
}) => {
  const { fetchDomainDetailInfo } = iamApiInstance({ token, iamEndpoint });
  const span = startServerTraceSpan(request, {
    name: 'settings.sso-policy-check',
    category: 'authentication',
    execution: 'serial',
    groupId: traceGroupId,
  });

  try {
    const res = await queryClient.fetchQuery({
      queryKey: [HOST_QUERY_KEY.GET_IAM_DOMAIN_DETAIL_INFO],
      queryFn: () => fetchDomainDetailInfo({ domainId }),
    });
    span.end('success');
    return res?.sso_enabled;
  } catch {
    span.end('fail_open');
    return false;
  }
};

/**
 * 비밀번호 재확인 가드
 * @description
 * - 비밀번호 재확인 가드를 통해 비밀번호 재확인 로직을 진행합니다.
 * - SSO 로그인 여부를 확인합니다.
 * - 비밀번호 재확인 성공 여부를 확인합니다.
 */
export const checkPasswordGuard = async ({
  context,
  queryClient,
  status,
  token,
  domainId,
  iamEndpoint,
}: {
  context: GetServerSidePropsContext;
  domainId: string;
  iamEndpoint: string;
  queryClient: QueryClient;
  status: StatusCode;
  token: string;
}) => {
  const { resolvedUrl = '', req } = context || {};
  const [, rawQueryString = ''] = resolvedUrl.split('?');
  const parsedQuery = (rawQueryString ? qs.parse(rawQueryString) : {}) as qs.ParsedQs;
  const requestCookies = parseCookies(req.headers.cookie || '');
  const destination = requestCookies[CookieKeyMap.DESTINATION_KEY] || '';

  const { headAuthCheckPassword } = iamApiInstance({ token, iamEndpoint });
  const traceGroupId = createServerTraceGroupId(req, 'serial');

  // SSO 로그인 여부 확인
  const isSsoEnabled = await checkSsoEnabled({
    domainId,
    queryClient,
    token,
    iamEndpoint,
    request: req,
    traceGroupId,
  });

  if (resolvedUrl.startsWith(SettingsRoutePath.CREDENTIALS + '?')) {
    return {
      redirect: {
        destination: mergePathWithQuery(SettingsRoutePath.CREDENTIALS_IAM_LIST, parsedQuery),
        permanent: false,
      },
    };
  }

  if (isSsoEnabled) {
    return {
      status,
    };
  }

  try {
    const passwordSessionSpan = startServerTraceSpan(req, {
      name: 'settings.password-session-check',
      category: 'authentication',
      execution: 'serial',
      groupId: traceGroupId,
    });

    // 비밀번호 재확인 성공 여부 조회
    try {
      await queryClient.fetchQuery({
        queryKey: [HOST_QUERY_KEY.HEAD_IAM_AUTH_CHECK_PASSWORD],
        queryFn: () => headAuthCheckPassword(),
      });
      passwordSessionSpan.end('success');
    } catch (error) {
      passwordSessionSpan.end('expected_rejection');
      throw error;
    }

    /**
     * 성공한 경우, 더이상 리다이렉트할 path 가 필요 없기 때문에 비밀번호 재확인 성공 여부 실패 당시 저장한 쿠키를 제거
     */
    appendContextCookies(context, [expireSetCookieString(CookieKeyMap.DESTINATION_KEY)]);

    if (resolvedUrl.startsWith(SettingsRoutePath.CHECK_PASSWORD)) {
      /**
       * apiV1AuthCheckPasswordHead 를 성공한 경우, 비밀번호 재확인 검증을 이미 진행한 케이스
       * /settings/check-password 페이지에서 비밀번호 입력한 후 넘어온 케이스에만 실제 이동할 페이지로 리다이렉트 수행
       */
      const destinationPath = destination || SettingsRoutePath.ACCOUNT;
      return {
        redirect: {
          destination: mergePathWithQuery(destinationPath, parsedQuery),
          permanent: false,
        },
      };
    }
  } catch {
    if (!resolvedUrl.startsWith(SettingsRoutePath.CHECK_PASSWORD)) {
      /**
       * apiV1AuthCheckPasswordHead 를 실패한 경우는 비밀번호 재확인 검증을 진행하지 않은 케이스
       * /settings/check-password 페이지로 리다이렉트하여 사용자가 비밀번호 재확인 페이지를 볼 수 있도록 pre-rendering
       * /settings/check-password 에서 검증 성공하면 사용자가 원래 진입하려고 했던 path 로 리다이렉트 해야하기 때문에 이는 쿠키에 세팅
       */
      appendContextCookies(context, [
        createSetCookieString(CookieKeyMap.DESTINATION_KEY, resolvedUrl),
      ]);

      return {
        redirect: {
          destination: mergePathWithQuery(SettingsRoutePath.CHECK_PASSWORD, parsedQuery),
          permanent: false,
        },
      };
    }
  }

  return { status };
};

checkPasswordGuard.displayName = 'checkPasswordGuard';
